<script setup lang="ts">
import { defineEmits } from "vue";
import BaseButton from "@/components/base/BaseButton.vue";
import AsideModal from "@/components/aside/AsideModal.vue";
import BaseInput from "@/components/base/BaseInput.vue";
import BaseFigureForIcon from "@/components/base/BaseFigureForIcon.vue";
import BaseIcon from "@/components/base/BaseIcon.vue";
import { useSettingsDetails } from "@/composables/settings/useSettingsDetails";

const emit = defineEmits<{
  (e: "submit"): void;
  (e: "back"): void;
}>();

const {
  changePeriod,
  clearData,
  email,
  format,
  handleEmailFocus,
  handleSubmit,
  isEmailError,
  isPeriodListShow,
  isValid,
  period,
  periods,
  validateEmail,
  isSelectPoint,
} = useSettingsDetails(emit);
</script>

<template>
  <div class="details-step__wrap">
    <div class="details-step__header">
      <div class="details-step__back" @click="clearData">
        <BaseFigureForIcon
          size="medium"
          color="white"
          icon="arrow"
          position="left"
          is-button
        />
      </div>
      <h3 class="details-step__details h3">
        {{ $t("settings.detailsView.title") }}
      </h3>
    </div>
    <div class="details-step">
      <div>
        <h2 class="h2 color-text-primary details-step__title">
          {{ $t("settings.detailsView.subtitle") }}
        </h2>
        <div class="details-step__period_drop-down">
          <div
            class="details-step__select caption-select color-white"
            @click="isPeriodListShow = true"
          >
            {{ period.title }}
            <BaseIcon
              name="more"
              :position="isPeriodListShow ? 'top' : 'bottom'"
              class="details-step__select-icon"
            />
          </div>
          <div class="details-step__select-list" v-if="isPeriodListShow">
            <div
              class="details-step__select-item color-grey-light caption-select--bold"
              :class="{ 'active-select': section.title === period.title }"
              v-for="section in periods"
              :key="section.key"
              @click="changePeriod(section)"
            >
              {{ section.title }}
            </div>
          </div>
          <AsideModal
            :is-show="isPeriodListShow"
            :is-white="true"
            @close="isPeriodListShow = false"
            class="details-step__select-list--mobile"
          >
            <p class="h1 color-secondary details-step__select_title">
              {{ $t("settings.detailsView.selectPeriod") }}
            </p>
            <div
              class="details-step__period_mobile"
              v-for="section in periods"
              :key="section.key"
              @click="changePeriod(section)"
              :class="{
                'active-select_background': section.title === period.title,
              }"
            >
              <p
                class="color-text-primary caption-select--bold"
                :class="{
                  'active-select_text': section.title === period.title,
                }"
              >
                {{ section.title }}
              </p>
              <BaseFigureForIcon
                :icon="isSelectPoint(section.title) ? 'check-sharp' : 'more'"
                size="small"
                :color="
                  isSelectPoint(section.title)
                    ? 'white-opacity-15'
                    : 'secondary'
                "
              />
            </div>
          </AsideModal>
        </div>
        <div>
          <BaseInput
            v-model="email"
            type="email"
            :has-error="isEmailError"
            @focus="handleEmailFocus"
            :placeholder="$t('settings.detailsView.emailPlaceholder')"
            @validation="validateEmail"
          />
          <p v-if="isEmailError && email" class="color-error hint bold">
            {{ $t("settings.detailsView.emailError") }}
          </p>
        </div>
      </div>
      <div>
        <div class="details-step__format">
          <p class="h4 color-text-primary">
            {{ $t("settings.detailsView.fileFormat") }}
          </p>
          <div class="details-step__format_radio">
            <div class="radio-item">
              <input
                type="radio"
                id="format_pdf"
                value="PDF"
                v-model="format"
              />
              <label for="format_pdf">PDF</label>
            </div>
            <div class="radio-item">
              <input
                type="radio"
                id="format_xlsx"
                value="xlsx"
                v-model="format"
              />
              <label for="format_xlsx">XLSX</label>
            </div>
          </div>
        </div>
        <BaseButton
          HEAD
          :title="$t('auth.phone.continue')"
          size="large"
          color="primary"
          :disabled="!isValid"
          @click="handleSubmit"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "@/assets/style/main";

.details-step {
  background: var(--color-white);
  padding: 0 24px 28px 24px;
  border-radius: 32px;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  width: 392px;
  height: 537px;
  margin: 32px 0;
  align-self: center;

  @media (max-width: 768px) {
    width: 100%;
    height: 100%;
    border-radius: 0;
    background-color: var(--color-bg-primary);
    margin-bottom: 100px;
  }

  &__wrap {
    width: 100%;
    max-width: 720px;
    display: flex;
    flex-direction: column;
    position: relative;

    @media (max-width: 960px) {
      max-width: 100%;
    }
    @media (max-width: 768px) {
      margin-top: 20px;
    }
  }

  &__header {
    display: grid;
    grid-template-columns: 0.8fr 1.4fr 0.8fr;

    @media (max-width: 960px) {
      grid-template-columns: 0.5fr 1.5fr 0.5fr;
      position: sticky;
      top: 0;
      padding: 13px;
      background: var(--color-bg-primary);
      background: var(--color-bg-primary);
    }
  }
  &__back {
    width: min-content;
  }

  &__details {
    display: flex;

    align-items: center;
    justify-content: center;

    @media (max-width: 768px) {
      opacity: 0;
      transition: opacity 0.2s;

      &.visible {
        opacity: 1;
      }
    }
  }

  &__back-button {
    display: flex;
    cursor: pointer;
    position: absolute;
    top: 0;
    left: 24px;
  }

  &__back-icon {
    height: 37px;
    width: 37px;
  }

  &__title {
    margin: 24px 0 32px 0;

    @media (max-width: 768px) {
      margin: 0 0 32px 0;
    }
  }

  &__period {
    &_icon {
      padding-right: 12px;
    }

    &_drop-down {
      margin: 0 0 32px 0;
      width: 100%;
      position: relative;
      display: flex;
    }

    &-arrow_mobile {
      width: 24px;
      height: 24px;
      background-color: var(--color-secondary);
      padding: 5px;
      border-radius: 24px;
    }

    &-arrow-current_mobile {
      width: 24px;
      height: 24px;
      background-color: rgba(255, 255, 255, 0.2);
      padding: 5px;
      border-radius: 24px;
    }

    &_mobile {
      display: flex;
      border-radius: 24px;
      min-height: 72px;
      align-items: center;
      justify-content: space-between;
      background-color: var(--color-bg-primary);
      padding: 17px 22px 17px 20px;
      margin: 4px 24px;
      cursor: pointer;
    }
  }

  &__select {
    height: 32px;
    padding: 6px 10px 8px 12px;
    border-radius: 12px;
    border: none;
    cursor: pointer;
    display: flex;
    flex-direction: row;
    gap: 10px;
    background: var(--color-secondary);
    --icon-size: 20px;

    &-list {
      box-shadow: 6px 6px 10px 0 rgba(0, 0, 0, 0.3),
        -6px -6px 10px 0 rgba(0, 0, 0, 0.3);
      width: 212px;
      background-color: #ffffff;
      padding: 24px 0 24px 16px;
      display: flex;
      flex-direction: column;
      gap: 20px;
      border-radius: 16px;
      position: absolute;
      top: 50px;

      @media (max-width: 768px) {
        display: none;
      }

      &--mobile {
        display: none;
        @media (max-width: 768px) {
          display: flex;
        }
      }
    }

    &_title {
      margin: 28px 24px 40px 24px;
    }
    &-item {
      cursor: pointer;
    }
  }

  &__input {
    background: var(--color-bg-primary);
    border-radius: 12px;
    border: none;
    height: 64px;
    width: 100%;
    font-family: inherit;
    cursor: pointer;
    padding-left: 20px;

    @media (max-width: 768px) {
      background-color: rgba(101, 104, 115, 0.1);
    }

    &:focus {
      outline: none;
    }

    &::placeholder {
      color: inherit;
      opacity: 1;
    }
  }

  &__submit-button {
    bottom: 28px;
    width: 100%;
    padding: 20px 0 18px 0;
    border-radius: 30px;
    border: none;
    background: var(--color-secondary);
    text-transform: uppercase;

    &:disabled {
      background: var(--color-grey-blue);
      cursor: not-allowed;
    }
  }

  &__format {
    display: flex;
    justify-content: space-between;
    margin-bottom: 24px;

    &_radio {
      display: flex;
      flex-direction: row;
      gap: 12px;
    }
  }
}

.radio-item {
  display: flex;
  align-items: center;
  position: relative;

  input[type="radio"] {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;

    &:checked + label:before {
      border-color: #ccd3dc;
      border-width: 4px;
      background: var(--color-secondary);
    }
  }

  label {
    position: relative;
    padding-left: 28px;
    cursor: pointer;
    font-size: 16px;
    color: var(--color-text-primary);
    user-select: none;

    &:before {
      content: "";
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 19px;
      height: 19px;
      border: 5px solid var(--color-grey-blue);
      border-radius: 50%;
      transition: all 0.2s ease;
      background-color: var(--color-bg-primary);
    }
  }
}

.hint {
  text-align: left;
  margin: 24px 0 0 0;
}

.error :deep(.email-input) {
  border: 1px solid var(--color-danger-primary);
  color: var(--color-danger-primary);
}

.active-select {
  color: var(--color-secondary);

  &_text {
    color: var(--color-white);
  }

  &_background {
    background-color: var(--color-secondary);
  }
}
</style>
